import { useEffect, useRef, useCallback } from "react";

/**
 * CustomCursor — Gold cursor follower with lightweight particles.
 * - Particles: CSS-based (box-shadow), minimised DOM impact
 * - Paused entirely when a modal/dialog is open (z-50 overlay)
 * - Auto-disables on touch devices & reduced motion
 */
const CURSOR_SIZE = 32;
const TRAIL_SIZE = 8;
const MAX_PARTICLES = 8;

const CustomCursor = () => {
  const cursorRef = useRef(null);
  const trailRef = useRef(null);
  const particlesRef = useRef([]);
  const rafRef = useRef(null);
  const pos = useRef({ x: -100, y: -100 });
  const smooth = useRef({ x: -100, y: -100 });
  const isHovering = useRef(false);
  const isModalActive = useRef(false);

  const isDisabled = useCallback(() => {
    return (
      window.matchMedia("(pointer: coarse)").matches ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }, []);

  const checkModal = useCallback(() => {
    return !!document.querySelector(
      '.fixed.inset-0.z-50, [class*="z-50"].fixed.inset-0'
    );
  }, []);

  const spawnParticle = useCallback((x, y) => {
    const container = trailRef.current?.parentElement;
    if (!container) return;

    const el = document.createElement("div");
    const size = 2 + Math.random() * 3;
    el.style.cssText = `
      position:fixed;pointer-events:none;z-index:9997;
      width:${size}px;height:${size}px;
      border-radius:50%;
      background:hsla(35,30%,70%,0.5);
      box-shadow:0 0 ${size * 2}px hsla(35,30%,75%,0.2);
      left:${x}px;top:${y}px;
      will-change:transform,opacity;
    `;
    container.appendChild(el);

    const p = {
      el,
      vx: (Math.random() - 0.5) * 4,
      vy: (Math.random() - 0.5) * 4 - 1,
      life: 1,
      decay: 0.025 + Math.random() * 0.025,
    };

    particlesRef.current.push(p);

    if (particlesRef.current.length > MAX_PARTICLES) {
      const old = particlesRef.current.shift();
      old.el.remove();
    }
  }, []);

  useEffect(() => {
    if (isDisabled()) return;

    const cursor = cursorRef.current;
    const trail = trailRef.current;
    if (!cursor || !trail) return;

    const updateParticles = () => {
      const active = particlesRef.current;
      for (let i = active.length - 1; i >= 0; i--) {
        const p = active[i];
        p.life -= p.decay;
        p.el.style.transform = `translate(${p.vx * (1 - p.life + 0.5) * 15}px, ${p.vy * (1 - p.life + 0.5) * 15}px) scale(${p.life})`;
        p.el.style.opacity = Math.max(0, p.life);

        if (p.life <= 0) {
          p.el.remove();
          active.splice(i, 1);
        }
      }
    };

    const animate = () => {
      const modalOpen = checkModal();
      isModalActive.current = modalOpen;

      if (modalOpen) {
        cursor.style.opacity = "0";
        trail.style.opacity = "0";
        // Clean up particles when modal opens
        particlesRef.current.forEach((p) => p.el.remove());
        particlesRef.current = [];
        rafRef.current = requestAnimationFrame(animate);
        return;
      }

      cursor.style.opacity = "1";
      trail.style.opacity = "1";

      smooth.current.x += (pos.current.x - smooth.current.x) * 0.18;
      smooth.current.y += (pos.current.y - smooth.current.y) * 0.18;

      const hover = isHovering.current;
      cursor.style.transform = `translate(${smooth.current.x}px, ${smooth.current.y}px) translate(-50%,-50%) scale(${hover ? 1.5 : 1})`;
      trail.style.transform = `translate(${pos.current.x}px, ${pos.current.y}px) translate(-50%,-50%) scale(${hover ? 0.5 : 1})`;

      // Continuous trail particles — throttled
      spawnParticle(
        pos.current.x + (Math.random() - 0.5) * 6,
        pos.current.y + (Math.random() - 0.5) * 6
      );

      updateParticles();
      rafRef.current = requestAnimationFrame(animate);
    };

    const onMove = (e) => {
      pos.current = { x: e.clientX, y: e.clientY };
    };

    const onHover = (e) => {
      const t = e.target.closest(
        'a, button, [role="button"], input, select, textarea, .card-luxury, .group, [data-cursor-hover]'
      );
      isHovering.current = !!t;
    };

    document.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseover", onHover);
    document.addEventListener("mouseenter", () => {
      cursor.style.opacity = "1";
      trail.style.opacity = "1";
      smooth.current = { ...pos.current };
    });
    document.addEventListener("mouseleave", () => {
      cursor.style.opacity = "0";
      trail.style.opacity = "0";
      particlesRef.current.forEach((p) => p.el.remove());
      particlesRef.current = [];
    });

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onHover);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      particlesRef.current.forEach((p) => p.el.remove());
      particlesRef.current = [];
    };
  }, [isDisabled, checkModal, spawnParticle]);

  if (typeof window !== "undefined" && isDisabled()) return null;

  return (
    <>
      <div
        ref={cursorRef}
        className="fixed pointer-events-none"
        style={{
          width: CURSOR_SIZE,
          height: CURSOR_SIZE,
          left: 0, top: 0, zIndex: 9999,
          opacity: 0,
          willChange: "transform",
        }}
      >
        <div className="w-full h-full rounded-full border border-[#D4AF7A]/60 bg-[#D4AF7A]/5 flex items-center justify-center">
          <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF7A]" />
        </div>
      </div>
      <div
        ref={trailRef}
        className="fixed pointer-events-none"
        style={{
          width: TRAIL_SIZE,
          height: TRAIL_SIZE,
          left: 0, top: 0, zIndex: 9998,
          opacity: 0,
          willChange: "transform",
        }}
      >
        <div className="w-full h-full rounded-full bg-[#D4AF7A]/30 blur-[1px]" />
      </div>
    </>
  );
};

export default CustomCursor;
